import React, { FunctionComponent, useState } from 'react';
import withStyles from 'react-jss';

import { ClassProp } from 'helpers/types';
import Table, {
  Direction,
  SortDefaultColumn,
  TableHeader,
} from 'components/Table/Table';

import { ExtendedSegment } from '../OfferSegments/OfferSegments';
import {
  OfferSummary,
  Segment,
  SegmentAction,
  Throttle,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/MerchantOffer.types';
import {
  renderDollarAmt,
  renderCashBackPercent,
  formatThrottleWithTooltip,
} from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/OfferSegments/components/OfferSegment/OfferSegment.helpers';
import OfferSegment from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/OfferSegments/components/OfferSegment';
import MarketPlacesPopUp from 'scenes/Home/components/OffersTable/components/MarketPlacesPopUp';
import { AttachedAudiences } from 'scenes/Home/scenes/MerchantStatusDetails/scenes/sections/MerchantOffers/components/SelectAudience';

import ActiveSegmentDates from './components/ActiveSegmentDates';
import OfferSegmentDeleteBtn from './components/OfferSegmentDeleteBtn';
import styles from './OfferSegmentsTable.styles';

type Styles = ClassProp<ReturnType<typeof styles>>;

type Props = {
  segments: ExtendedSegment[];
  offer?: OfferSummary;
  disablePulse: boolean;
  activeSegment: SegmentAction;
  setActiveSegment: React.Dispatch<React.SetStateAction<SegmentAction>>;
  attachedAudiences: AttachedAudiences;
  classes: Styles;
};

const renderCashBack = ({ cashbackAmount, cashbackPercentage }: Segment) => {
  if (cashbackAmount) {
    return renderDollarAmt(cashbackAmount);
  }
  return renderCashBackPercent(cashbackPercentage);
};

const tableHeader: TableHeader[] = [
  {
    label: 'Segment Type',
    source: ['segmentType'],
    sortable: ['segmentType'],
  },
  {
    label: 'Promo Type',
    source: ['promotionType'],
    sortable: ['promotionType'],
  },
  {
    label: 'Priority',
    source: ['segmentPriority'],
    sortable: ['segmentPriority'],
  },
  {
    label: 'Active Dates',
    source: ['endDate'],
    component: {
      Element: ActiveSegmentDates,
    },
  },
  {
    label: 'Cashback',
    source: [],
    transform: (segment: Segment) => renderCashBack(segment),
  },
  {
    label: 'Marketplaces',
    source: ['marketplaces'],
    component: {
      Element: MarketPlacesPopUp,
    },
  },
  {
    label: 'Throttle',
    source: ['throttle'],
    transform: (throttle: Throttle | string) =>
      typeof throttle === 'string'
        ? throttle
        : formatThrottleWithTooltip(throttle),
  },
  {
    label: 'Audience',
    source: ['audience', 'name'],
  },
  {
    label: 'Hidden',
    source: ['hideOfferForSegment'],
    transform: (hideOfferForSegment: boolean) =>
      hideOfferForSegment ? 'Yes' : 'No',
  },
  {
    label: 'Actions',
    source: [],
    component: {
      Element: OfferSegmentDeleteBtn,
    },
  },
];

const sortDefaultColumn: SortDefaultColumn = {
  name: 'Segment Type',
  sortable: ['segmentType'],
  direction: Direction.ASCENDING,
};

const OfferSegmentsTable: FunctionComponent<Props> = ({
  segments,
  offer,
  activeSegment,
  setActiveSegment,
  attachedAudiences,
  disablePulse,
  classes,
}) => {
  const [rowNumber, setRowNumber] = useState(0);

  const selectedRowHandler = (rowId: string) => {
    for (const [index, segment] of segments.entries()) {
      if (segment.uuid === rowId) {
        setRowNumber(index);
      }
    }
  };

  return (
    <Table
      tableHeader={tableHeader}
      sortDefaultColumn={sortDefaultColumn}
      data={segments}
      itemsKey="uuid"
      selectedrowCallback={selectedRowHandler}
      classes={classes}
      expandedViewComponent={
        <OfferSegmentWrapper
          offer={offer}
          segments={segments}
          disablePulse={disablePulse}
          selectedRow={rowNumber}
          activeSegment={activeSegment}
          setActiveSegment={setActiveSegment}
          attachedAudiences={attachedAudiences}
          classes={classes}
          expired={false}
        />
      }
    />
  );
};

type OfferSegmentWrapperProps = {
  segments: ExtendedSegment[];
  offer?: OfferSummary;
  selectedRow: number;
  disablePulse: boolean;
  activeSegment: SegmentAction;
  setActiveSegment: React.Dispatch<React.SetStateAction<SegmentAction>>;
  attachedAudiences: AttachedAudiences;
  classes: Styles;
  expired: boolean;
};

const OfferSegmentWrapper: FunctionComponent<OfferSegmentWrapperProps> = ({
  segments,
  disablePulse,
  activeSegment,
  setActiveSegment,
  attachedAudiences,
  selectedRow,
  offer,
}) => {
  const segment = segments[selectedRow];
  return (
    <OfferSegment
      segment={segment}
      expired={segment.expired}
      deleted={segment.deleted}
      disablePulse={disablePulse}
      activeSegment={activeSegment}
      setActiveSegment={setActiveSegment}
      attachedAudiences={attachedAudiences}
      offer={offer}
    />
  );
};

export default withStyles(styles)(OfferSegmentsTable);
